import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EnvironmentInjector,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  createComponent,
} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { ResolvedSectionLoader } from '../models/loading.models';
import { LoaderRegistryService } from '../services/loader-registry.service';
import { AppSectionLoaderOverlayComponent } from '../components/app-section-loader-overlay/app-section-loader-overlay.component';

@Directive({
  selector: '[appSectionLoaderHost]',
  standalone: false,
})
export class AppSectionLoaderHostDirective
  implements OnInit, OnChanges, OnDestroy
{
  @Input('appSectionLoaderHost') scopeKey = '';

  private overlayComponentRef?: ComponentRef<AppSectionLoaderOverlayComponent>;
  private sectionLoaderSubscription?: Subscription;
  private managedHostPosition = false;

  constructor(
    private readonly applicationRef: ApplicationRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly environmentInjector: EnvironmentInjector,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly loaderRegistryService: LoaderRegistryService,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.ensureHostPositioning();
    this.attachOverlayComponent();
    this.bindSectionLoader();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scopeKey'] && !changes['scopeKey'].firstChange) {
      this.bindSectionLoader();
    }
  }

  ngOnDestroy(): void {
    this.sectionLoaderSubscription?.unsubscribe();

    if (this.overlayComponentRef) {
      this.applicationRef.detachView(this.overlayComponentRef.hostView);
      this.overlayComponentRef.destroy();
      this.overlayComponentRef = undefined;
    }

    if (this.managedHostPosition) {
      this.renderer.removeStyle(this.elementRef.nativeElement, 'position');
    }

    this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-busy');
  }

  private bindSectionLoader(): void {
    this.sectionLoaderSubscription?.unsubscribe();

    const normalizedScopeKey = this.scopeKey.trim();

    if (!normalizedScopeKey) {
      this.syncSectionLoaderState(null, null);
      return;
    }

    this.sectionLoaderSubscription = combineLatest([
      this.loaderRegistryService.selectSectionLoader(normalizedScopeKey),
      this.loaderRegistryService.selectVisibleSectionLoader(normalizedScopeKey),
    ]).subscribe(([activeSectionLoader, visibleSectionLoader]) => {
      this.syncSectionLoaderState(activeSectionLoader, visibleSectionLoader);
    });
  }

  private attachOverlayComponent(): void {
    if (this.overlayComponentRef) {
      return;
    }

    const overlayHostElement = this.renderer.createElement(
      'myt-app-section-loader-overlay-host'
    );

    this.overlayComponentRef = createComponent(AppSectionLoaderOverlayComponent, {
      environmentInjector: this.environmentInjector,
      hostElement: overlayHostElement,
    });

    this.applicationRef.attachView(this.overlayComponentRef.hostView);
    this.renderer.appendChild(this.elementRef.nativeElement, overlayHostElement);
  }

  private syncSectionLoaderState(
    activeSectionLoader: ResolvedSectionLoader | null,
    visibleSectionLoader: ResolvedSectionLoader | null
  ): void {
    if (activeSectionLoader) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-busy', 'true');
    } else {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-busy');
    }

    this.overlayComponentRef?.setInput('sectionLoader', visibleSectionLoader);
    this.overlayComponentRef?.changeDetectorRef.detectChanges();
  }

  private ensureHostPositioning(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
      this.managedHostPosition = true;
      return;
    }

    const defaultView = this.document.defaultView;

    if (!defaultView) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
      this.managedHostPosition = true;
      return;
    }

    const computedHostStyle = defaultView.getComputedStyle(
      this.elementRef.nativeElement
    );

    if (computedHostStyle.position === 'static') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
      this.managedHostPosition = true;
    }
  }
}
